/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, ViewEncapsulation, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SwaggerUIService } from '../shared/services/swagger/swagger-ui.service';
import { DynamicOpenApiService } from '../api-mesh/services/dynamic-openapi.service';
import { ApiOperation } from '../api-mesh/services/api-discoverability.service';

@Component({
  selector: 'app-dynamic-api-viewer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dynamic-api-viewer.component.html',
  styleUrls: ['./dynamic-api-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DynamicApiViewerComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('swaggerContainer', { static: false }) swaggerContainer!: ElementRef;

  @Input() operation: ApiOperation | null = null;
  @Input() isEmbedded: boolean = false;
  @Output() backToExplorer = new EventEmitter<void>();

  loading = true;
  error: string | null = null;
  swaggerUIInstance: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private swaggerUIService: SwaggerUIService,
    private dynamicOpenApiService: DynamicOpenApiService
  ) { }

  ngOnInit(): void {
    // Scroll to top when component is initialized
    this.scrollToTop();

    // If not embedded, get operation data from query parameters
    if (!this.isEmbedded) {
      this.route.queryParams.subscribe(params => {
        const operationData = params['operation'];
        if (operationData) {
          try {
            this.operation = JSON.parse(decodeURIComponent(operationData));
            this.loading = false;
          } catch (error) {
            this.error = 'Invalid operation data provided';
            this.loading = false;
          }
        } else {
          this.error = 'No operation data provided';
          this.loading = false;
        }
      });
    } else {
      // For embedded mode, operation is provided via @Input
      this.loading = this.operation ? false : true;
      if (!this.operation) {
        this.error = 'No operation data provided';
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['operation'] && this.operation) {
      this.loading = false;
      this.error = null;

      // Scroll to top when operation changes (for embedded mode)
      this.scrollToTop();

      // If view is initialized, reinitialize SwaggerUI
      if (this.swaggerContainer) {
        setTimeout(() => this.initializeSwaggerUI(), 100);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.operation && !this.loading) {
      setTimeout(() => this.initializeSwaggerUI(), 100);
    }
  }

  async initializeSwaggerUI(): Promise<void> {
    if (!this.operation || !this.swaggerContainer) {
      this.error = 'Missing operation data or Swagger container';
      return;
    }

    try {
      console.log('Generating dynamic OpenAPI spec for operation:', this.operation);

      // Generate dynamic OpenAPI specification
      const dynamicSpec = this.dynamicOpenApiService.generateOpenApiSpec(this.operation);

      console.log('Generated OpenAPI spec:', dynamicSpec);

      // Clear existing Swagger UI
      if (this.swaggerUIInstance) {
        this.swaggerUIService.clearSwaggerUI(this.swaggerContainer.nativeElement);
      }

      // Initialize Swagger UI with the dynamic specification
      this.swaggerUIInstance = await this.swaggerUIService.initializeSwaggerUI(
        this.swaggerContainer.nativeElement,
        dynamicSpec
      );

      console.log('Dynamic Swagger UI initialized successfully');

      // Auto-activate "Try it out" button after SwaggerUI is ready
      setTimeout(() => this.autoActivateTryItOut(), 1000);

    } catch (error) {
      console.error('Error initializing dynamic Swagger UI:', error);
      this.error = `Failed to initialize Swagger UI: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }

  retrySwaggerUI(): void {
    this.error = null;
    this.initializeSwaggerUI();
  }

  /**
   * Automatically activate the "Try it out" button for the operation
   */
  private autoActivateTryItOut(): void {
    try {
      // Find and click the "Try it out" button in the Swagger UI
      const swaggerContainer = this.swaggerContainer.nativeElement;

      // First, expand the operation by clicking the control arrow
      const controlArrow = swaggerContainer.querySelector('.opblock-control-arrow') as HTMLElement;
      if (controlArrow) {
        const operationBlock = controlArrow.closest('.opblock') as HTMLElement;
        if (operationBlock && !operationBlock.classList.contains('is-open')) {
          console.log('Expanding operation block');
          controlArrow.click();
        }
      }

      // Look for the Try it out button
      const tryItOutButton = swaggerContainer.querySelector('.btn.try-out__btn') as HTMLElement;

      if (tryItOutButton && !tryItOutButton.classList.contains('cancel')) {
        console.log('Auto-activating "Try it out" button');
        tryItOutButton.click();

        console.log('Successfully auto-activated "Try it out" button');
      } else if (tryItOutButton && tryItOutButton.classList.contains('cancel')) {
        console.log('"Try it out" is already active');
      } else {
        console.log('Could not find "Try it out" button, retrying in 500ms');
        // Retry after a short delay if the button isn't found yet
        setTimeout(() => this.autoActivateTryItOut(), 500);
      }
    } catch (error) {
      console.warn('Failed to auto-activate "Try it out" button:', error);
    }
  }

  /**
   * Format operation name by adding spaces before capital letters
   * Example: 'GetDevice' becomes 'Get Device'
   */
formatOperationName(name: string): string {
    if (!name) return '';
    // Handle special cases like 'URL' as a single word, then add spaces before other capital letters
    return name
        .replace(/URL/g, 'Url') // Temporarily convert URL to Url
        .replace(/([A-Z])/g, ' $1')
        .replace(/Url/g, 'URL') // Convert back to URL
        .trim();
}

  goBack(): void {
    if (this.isEmbedded) {
      this.backToExplorer.emit();
    } else {
      this.router.navigate(['/api-mesh'], { fragment: 'api-explorer' });
    }
  }

  /**
   * Scroll to the top of the page
   */
  private scrollToTop(): void {
    // Scroll the main window/document to top
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Also scroll the document element to top as fallback
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  ngOnDestroy(): void {
    if (this.swaggerUIInstance) {
      this.swaggerUIInstance = null;
    }
  }
}
